import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import CustomDatePicker from '../components/CustomDatePicker';

const EmiConfigurationForm = ({ 
  creditAmount, 
  onEmiChange, 
  emiData, 
  currencySymbol = '$' 
}) => {
  const [duration, setDuration] = useState(emiData?.duration || '');
  const [frequency, setFrequency] = useState(emiData?.frequency || 'monthly');
  const [interestRate, setInterestRate] = useState(emiData?.interestRate || '');
  const [startDate, setStartDate] = useState(emiData?.startDate || '');
  const [calculatedEmi, setCalculatedEmi] = useState(0);

  const calculateEmi = (principal, rate, months) => {
    if (!principal || !rate || !months) return 0;
    
    const monthlyRate = rate / 100 / 12;
    if (monthlyRate === 0) {
      return principal / months;
    }
    
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                (Math.pow(1 + monthlyRate, months) - 1);
    
    return Math.round(emi * 100) / 100;
  };

  useEffect(() => {
    if (creditAmount && duration && interestRate) {
      const months = frequency === 'weekly' ? Math.ceil(duration * 4.33) : duration;
      const emi = calculateEmi(parseFloat(creditAmount), parseFloat(interestRate), months);
      setCalculatedEmi(emi);
    }
  }, [creditAmount, duration, frequency, interestRate]);

  useEffect(() => {
    onEmiChange({
      duration: parseInt(duration) || 0,
      frequency,
      interestRate: parseFloat(interestRate) || 0,
      startDate,
      calculatedEmi,
    });
  }, [duration, frequency, interestRate, startDate, calculatedEmi, onEmiChange]);

  const getTotalInterest = () => {
    if (!calculatedEmi || !duration) return 0;
    const totalPayment = calculatedEmi * duration;
    return totalPayment - parseFloat(creditAmount || 0);
  };

  return (
    <Card sx={{ mt: 2, backgroundColor: 'background.paper' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          EMI Configuration
        </Typography>
        
        <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="EMI Duration"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 1, max: 60 }}
              helperText={`Duration in ${frequency === 'weekly' ? 'weeks' : 'months'}`}
              required
            />
            
            <FormControl>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                label="Frequency"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Interest Rate (% per annum)"
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              inputProps={{ min: 0, max: 50, step: 0.1 }}
              required
            />
            
            <CustomDatePicker
              label="EMI Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              helperText="Optional - defaults to today"
            />
          </Box>
        </Box>

        {calculatedEmi > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                EMI Calculation Summary
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Credit Amount:</strong> {currencySymbol}{parseFloat(creditAmount || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>EMI Amount:</strong> {currencySymbol}{calculatedEmi.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Interest:</strong> {currencySymbol}{getTotalInterest().toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Payment:</strong> {currencySymbol}{(calculatedEmi * duration).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {duration && interestRate && !calculatedEmi && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please ensure all EMI fields are filled correctly to calculate the installment amount.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EmiConfigurationForm;
